import GenericLoader from './genericLoader'
import RenderEventDispatcher from './eventDispatcher'

class PollingLoader extends RenderEventDispatcher {
  // Availalbe options:
  //   `timeout` - Maximum total time in seconds for a load attempt (default 10)
  //   `tries`   - Maximum number of attempts to make per part (default 10)
  constructor(options) {
    super()
    // Init
    this.load = this.load.bind(this)
    this.reset = this.reset.bind(this)
    this.incompleteCount = this.incompleteCount.bind(this)
    this.maybeFinished = this.maybeFinished.bind(this)
    this.completePart = this.completePart.bind(this)
    this.failPart = this.failPart.bind(this)
    if (!options) {
      options = {}
    }
    this.reset()
    if (!options.timeout) {
      options.timeout = 10
    } // Total timeout per part
    if (!options.tries) {
      options.tries = 10
    } // Tries per piece
    if (!options.maxLateJobs) {
      options.maxLateJobs = 1
    } // Maximum number of jobs that can be late to the timeout
    ;({timeout: this.timeout, tries: this.tries, status: this.status, maxLateJobs: this.maxLateJobs} = options)

    // Bind result handlers
    this.addEventListener('ready', () => {
      this.cb(false, this)
      return (this.cb = () => {})
    })
    this.addEventListener('error', () => {
      this.cb(true, this)
      return (this.cb = () => {})
    })
  }

  // Load driver
  //
  // fragments - An Object describing names and URLs of framgments
  // callback  - A callback in the form `callback: (error, self) ->`
  //             invoked when the operation either succeeds or fails
  load(fragments, callback) {
    this.reset()
    if (callback) {
      this.cb = callback
    }
    this.time.start = Date.now()
    this.time.end = undefined

    // Populate `@parts` with our sheduled fetch jobs.
    //
    // Each 'part' is a plain Object with the request data, `.name`, `.url`,
    // the resulting geometry, `.geom`, the selected loader instance, `.loader`,
    // and a driver closure `.load()`
    //
    // Success callbacks are wired to `@completePart(part, data)` which stores
    // the produced data in `part`, and tracks the overall job completion through
    // `@maybeFinished`
    //
    // Error callbacks are wired to `@failPart(part)` which fails the entire group
    // operation.
    //
    // Invocation of `@cb`, bound to any passed `callback` is handled by event
    // handlers bound in the `constructor`
    return Object.keys(fragments).map(name => {
      const url = fragments[name]
      const part = (this.parts[name] = {
        name,
        url,
        geom: undefined,
        loader: GenericLoader.loaderForUrl(this.status, url),
        load: () => part.loader.load(part.url, {attempts: this.tries})
      })

      part.loader.addEventListener('load', event => {
        this.completePart(part, event.content)
      })
      part.loader.addEventListener('error', () => {
        this.failPart(part)
      })
      part.loader.addEventListener('error:fatal', () => {
        this.failPart(part)
      })

      return part.load()
    })
  }

  // Internal:
  // Reset internal state and bound callbacks.
  reset() {
    this.parts = {}
    this.time = {start: undefined, end: undefined}
    if (this.cb == null) {
      this.cb = function () {}
    }
    return this.cb
  }

  // Internal:
  // Returns the count of parts that remain incomplete.
  incompleteCount() {
    let done = 0
    let total = 0
    for (const name of Object.keys(this.parts || {})) {
      const part = this.parts[name]
      total += 1
      if (part.geom != null) {
        done += 1
      }
    }
    return total - done
  }

  // Internal:
  // Called to see if all of the parts in @parts
  // have a `geom` member, indicating that the group has
  // completed, if so the 'success' event is fired.
  // If we are not done, the `otherwise` callback
  // is invoked to continue the search.
  //
  // The CPS-style branch is there to avoid deffering
  // the early termination logic to the caller in the case
  // of an error.
  maybeFinished(otherwise) {
    if (otherwise == null) {
      otherwise = function () {}
    }
    if (Date.now() >= this.time.start + this.timeout * 1000) {
      // If we're finna finish the last part, don't error
      // out for a timeout, just let the finish happen.
      if (this.incompleteCount() > this.maxLateJobs) {
        this.dispatchEvent({type: 'error', data: 'timeout'})
      }
    }
    let done = true
    for (const name of Object.keys(this.parts)) {
      const part = this.parts[name]
      done = done && !(part.geom == null)
    }

    // Call either the finish callback or the otherwise branch
    if (done) {
      this.time.end = Date.now()
      window.debug('All parts Ready')
      this.dispatchEvent({type: 'ready', info: this})
    } else {
      otherwise()
    }
  }

  // Internal:
  // Called when a part is succesfully loaded.
  // The geometry is attached to the part and
  // finish checks are run in @maybeFinished
  completePart(part, geom) {
    window.debug('completePart:', part, geom)
    part.geom = geom
    this.maybeFinished()
  }

  // Internal:
  // Called when a part fails. A single failing part
  // is enough to fail the group.
  failPart(part) {
    window.debug('failPart():', part)
    this.dispatchEvent({type: 'error', info: `Too many tries for ${part.url}`})
  }
}

export default PollingLoader
